import { type FC, useEffect } from "react";

import {
  useAssessedCountDataWithNotSubmitted,
  useAssessmentMode,
  useAssessmentNavData,
  useFirstAssessableResponseId,
} from "../../../../utils/assessment";
import { useResponsesForPage } from "../responses";

import { getOrRunAiAssessment } from "components/ai/queries/assessment-cache";
import { ACCESS_ID_QUERY_PARAM, SUBMISSION_ID_QUERY_PARAM } from "components/constants";
import { AssessWithAIModeButton } from "components/materials/page/assessment/AssessWithAIModeButton";
import { AssessmentGradingForm } from "components/materials/page/assessment/AssessmentGradingForm";
import { AssessmentGradingLastPanel } from "components/materials/page/assessment/AssessmentGradingLastPanel";
import { AssessmentGradingNoSubmission } from "components/materials/page/assessment/AssessmentGradingNoSubmission";
import { ContentSidebarHeader } from "components/materials/sidebar/ContentSidebarHeader";
import { ContentSidebarMain } from "components/materials/sidebar/ContentSidebarMain";
import { OverlayContainer } from "components/materials/sidebar/OverlayContainer";
import { PageType } from "components/server-types";
import { t } from "i18n/i18n";
import { CloseButton } from "mds/components/CloseButton";
import { AssessPointsMarkerIcon } from "mds/icons";
import { useAppSelector } from "store/index";
import { selectFullSubmissionById, selectIsAssessWithAIModeEnabled } from "store/selectors";
import { useQueryParamsManager } from "utils/routing";

interface AssessmentGradingViewProps {
  page: PageType;
}

export const AssessmentGradingView: FC<AssessmentGradingViewProps> = ({ page }) => {
  const { getQueryParam, setQueryParams } = useQueryParamsManager();
  const assessedCountData = useAssessedCountDataWithNotSubmitted(page);
  const { onExitAssessmentModeWithModal } = useAssessmentMode(page);

  const { responses } = useResponsesForPage(page);
  const firstId = useFirstAssessableResponseId(page, responses);
  const { nextURL, prevURL, submissionForNextUser, onBack, isLastPanel } =
    useAssessmentNavData(page);

  const accessParamId = getQueryParam(ACCESS_ID_QUERY_PARAM);
  const submissionId = getQueryParam(SUBMISSION_ID_QUERY_PARAM);
  const submission = useAppSelector((s) => selectFullSubmissionById(s, submissionId));

  // Ensures AI assessments for the current and next submissions are pre-fetched
  // TODO: Eventually, we could make this pre-fetch assessments for every submission for the current page
  const isAssessWithAIModeEnabled = useAppSelector(selectIsAssessWithAIModeEnabled);
  const nextSubmissionId = submissionForNextUser?.id;
  const nextSubmission = useAppSelector((s) => selectFullSubmissionById(s, nextSubmissionId));

  useEffect(() => {
    if (isAssessWithAIModeEnabled && submission && !isLastPanel) {
      getOrRunAiAssessment(submission);
    }
  }, [isAssessWithAIModeEnabled, submission, isLastPanel]);

  useEffect(() => {
    if (isAssessWithAIModeEnabled && nextSubmission && !isLastPanel) {
      getOrRunAiAssessment(nextSubmission);
    }
  }, [isAssessWithAIModeEnabled, nextSubmission, isLastPanel]);

  useEffect(() => {
    if (!accessParamId && firstId) {
      setQueryParams({ [ACCESS_ID_QUERY_PARAM]: firstId });
    }
    // eslint-disable-next-line no-object-deps/no-object-deps
  }, [accessParamId, firstId, setQueryParams]);

  // There are three different views in the grading view: the grading form, the no submission view, and the last panel.
  return (
    <OverlayContainer className="bg-white lg:max-w-[400px]">
      <ContentSidebarHeader panelType="overlay" visibleOnMobile>
        <div className="flex w-full flex-row items-center justify-between">
          <h3 className="flex flex-row items-center gap-2">
            <AssessPointsMarkerIcon className="text-green" />
            {t("glossary.assessment")}
          </h3>

          <div className="flex items-center">
            <CloseButton
              eventName="Assessment - Stop"
              size="s"
              title={t("assessment.stop_assessing")}
              onClick={onExitAssessmentModeWithModal(
                assessedCountData.count,
                assessedCountData.total,
              )}
            />
          </div>
        </div>
      </ContentSidebarHeader>

      <ContentSidebarMain bottomPadding={false}>
        <div className="mt-2 flex flex-col gap-4">
          {isLastPanel ? (
            <AssessmentGradingLastPanel
              assessedCountData={assessedCountData}
              hideButtons={!!page.assessments_published_at}
              page={page}
              onBack={onBack}
            />
          ) : !submission ? (
            <AssessmentGradingNoSubmission />
          ) : (
            <>
              <AssessWithAIModeButton />
              <AssessmentGradingForm nextURL={nextURL} page={page} prevURL={prevURL} />
            </>
          )}
        </div>
      </ContentSidebarMain>
    </OverlayContainer>
  );
};
